import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Paperclip, CheckCircle2, X, Calculator } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { api, submitLead } from "../lib/api";
import { buildWhatsAppLink } from "../lib/constants";

const SERVICE_OPTIONS = [
    "Logo",
    "Branding",
    "Graphic Design",
    "Printing",
    "Packaging",
    "Signage",
    "Social Media",
    "Catalogue",
    "Brochure",
    "Website",
    "Other",
];

const BUDGET_OPTIONS = [
    "Under ₹5,000",
    "₹5,000 – ₹15,000",
    "₹15,000 – ₹50,000",
    "₹50,000 – ₹1,00,000",
    "Above ₹1,00,000",
    "Not sure yet",
];

const initial = {
    name: "",
    business_name: "",
    phone: "",
    whatsapp: "",
    email: "",
    requirement: "",
    quantity: "",
    specifications: "",
    deadline: "",
    budget: "",
    message: "",
    website: "",
};

const inputCls =
    "bg-black/40 border border-white/10 focus:border-gold rounded-xl px-4 py-3.5 text-white outline-none w-full transition-colors font-inter text-sm placeholder:text-zinc-600";
const selectCls =
    "bg-black/40 border border-white/10 rounded-xl px-4 py-6 text-white font-inter hover:border-gold/40 focus:border-gold focus:ring-0 data-[placeholder]:text-zinc-600";

const GetQuotePage = () => {
    useDocumentMeta({
        title: "Get a Quote — Design & Printing in Nagpur | Raj Graphics",
        description:
            "Request a detailed quote for logo design, branding, printing, packaging, signage or website work. Share your requirement and Raj Graphics replies with clear pricing.",
        canonical: "/get-quote",
    });

    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const fileRef = useRef(null);

    const setField = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const onFilePick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) {
            toast.error("File too large", { description: "Maximum size is 5 MB." });
            e.target.value = "";
            return;
        }
        setFile(f);
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please enter your name";
        const digits = form.phone.replace(/\D/g, "");
        if (digits.length < 7) e.phone = "Please enter a valid mobile number";
        if (form.email && !/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(form.email.trim()))
            e.email = "Please enter a valid email";
        if (!form.requirement) e.requirement = "Select the service you need";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            let attachment_url = "";
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads/quote", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                attachment_url = data.url;
            }
            await submitLead({
                name: form.name.trim(),
                phone: form.phone.trim(),
                whatsapp: form.whatsapp.trim(),
                email: form.email.trim(),
                business_name: form.business_name.trim(),
                requirement: form.requirement,
                quantity: form.quantity.trim(),
                specifications: form.specifications.trim(),
                deadline: form.deadline.trim(),
                budget: form.budget,
                message: form.message.trim(),
                source: "get-quote",
                attachment_url,
                website: form.website,
            });
            setDone({ ...form });
            window.scrollTo({ top: 0 });
        } catch (_err) {
            toast.error("Couldn't send your request", {
                description: "Please try again, or message us directly on WhatsApp.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const successWaLink = done
        ? buildWhatsAppLink(
              `Hi Raj Graphics! I just submitted a quote request on your website.\n• Name: ${done.name}\n• Service: ${done.requirement}${done.quantity ? `\n• Quantity: ${done.quantity}` : ""}${done.deadline ? `\n• Deadline: ${done.deadline}` : ""}\nLooking forward to your quote.`,
          )
        : "";

    return (
        <main data-testid="get-quote-page" className="bg-[#050505]">
            <section className="relative pt-36 pb-10 md:pt-44 md:pb-12 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Get a Quote
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="get-quote-headline"
                    >
                        Tell us what you need. <span className="gold-gradient-text">Get clear pricing.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Share your requirement below — the more detail, the sharper the quote. We
                        usually reply within a few business hours.
                    </motion.p>
                    <Link
                        to="/quote"
                        data-testid="link-instant-estimator"
                        className="mt-5 inline-flex items-center gap-2 font-inter text-sm text-zinc-400 hover:text-gold transition-colors"
                    >
                        <Calculator size={15} className="text-gold" />
                        In a hurry? Try the instant price estimator instead
                        <ArrowRight size={14} />
                    </Link>
                </div>
            </section>

            <section className="relative pb-20 md:pb-28 pt-8">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    {done ? (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="quote-success-panel"
                            className="rounded-2xl bg-gradient-to-b from-gold/[0.10] to-gold/[0.02] border border-gold/30 p-8 md:p-12 text-center"
                        >
                            <CheckCircle2 size={44} className="text-gold mx-auto mb-6" strokeWidth={1.5} />
                            <h2 className="font-poppins font-semibold text-white text-2xl md:text-3xl">
                                Request received, {done.name.split(" ")[0]}.
                            </h2>
                            <p className="mt-4 font-inter text-zinc-400 max-w-md mx-auto leading-relaxed">
                                We'll review your {done.requirement.toLowerCase()} requirement and
                                get back with a detailed quote. Want it even faster?
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                                <a
                                    href={successWaLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="quote-success-whatsapp"
                                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fbb59] text-white font-medium font-inter px-7 py-3.5 rounded-full transition-colors"
                                >
                                    Continue on WhatsApp <ArrowRight size={17} />
                                </a>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDone(null);
                                        setForm(initial);
                                        setFile(null);
                                    }}
                                    data-testid="quote-success-new-request"
                                    className="inline-flex items-center justify-center gap-2 border border-white/15 text-white hover:border-gold hover:text-gold font-medium font-inter px-7 py-3.5 rounded-full transition-colors"
                                >
                                    Send another request
                                </button>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.form
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            onSubmit={onSubmit}
                            noValidate
                            data-testid="get-quote-form"
                            className="bg-white/[0.02] border border-white/10 rounded-2xl p-7 md:p-10"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Your Name *</label>
                                    <input type="text" value={form.name} onChange={(e) => setField("name", e.target.value)} autoComplete="name" placeholder="Full name" className={inputCls} data-testid="gq-input-name" />
                                    {errors.name && <p className="mt-2 text-xs text-red-400 font-inter">{errors.name}</p>}
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Business Name</label>
                                    <input type="text" value={form.business_name} onChange={(e) => setField("business_name", e.target.value)} placeholder="Your brand or company" className={inputCls} data-testid="gq-input-business" />
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Mobile Number *</label>
                                    <input type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} autoComplete="tel" placeholder="+91 98xxx xxxxx" className={inputCls} data-testid="gq-input-phone" />
                                    {errors.phone && <p className="mt-2 text-xs text-red-400 font-inter">{errors.phone}</p>}
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">WhatsApp Number</label>
                                    <input type="tel" value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} placeholder="Same as mobile? Leave blank" className={inputCls} data-testid="gq-input-whatsapp" />
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Email</label>
                                    <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} autoComplete="email" placeholder="you@example.com" className={inputCls} data-testid="gq-input-email" />
                                    {errors.email && <p className="mt-2 text-xs text-red-400 font-inter">{errors.email}</p>}
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Service Required *</label>
                                    <Select value={form.requirement} onValueChange={(v) => setField("requirement", v)}>
                                        <SelectTrigger className={selectCls} data-testid="gq-select-service">
                                            <SelectValue placeholder="Select a service" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-950 border border-white/10 text-white max-h-72">
                                            {SERVICE_OPTIONS.map((s) => (
                                                <SelectItem key={s} value={s} data-testid={`gq-service-${s.toLowerCase().replace(/\s+/g, "-")}`} className="focus:bg-gold/10 focus:text-gold">
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.requirement && <p className="mt-2 text-xs text-red-400 font-inter">{errors.requirement}</p>}
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Quantity</label>
                                    <input type="text" value={form.quantity} onChange={(e) => setField("quantity", e.target.value)} placeholder="e.g. 500 cards, 2 banners" className={inputCls} data-testid="gq-input-quantity" />
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Deadline</label>
                                    <input type="text" value={form.deadline} onChange={(e) => setField("deadline", e.target.value)} placeholder="e.g. within 10 days" className={inputCls} data-testid="gq-input-deadline" />
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Approximate Budget</label>
                                    <Select value={form.budget} onValueChange={(v) => setField("budget", v)}>
                                        <SelectTrigger className={selectCls} data-testid="gq-select-budget">
                                            <SelectValue placeholder="Select a range" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-950 border border-white/10 text-white">
                                            {BUDGET_OPTIONS.map((b) => (
                                                <SelectItem key={b} value={b} className="focus:bg-gold/10 focus:text-gold">
                                                    {b}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Size / Specifications</label>
                                    <input type="text" value={form.specifications} onChange={(e) => setField("specifications", e.target.value)} placeholder="e.g. 6x3 ft flex, A5 brochure" className={inputCls} data-testid="gq-input-specs" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Message</label>
                                    <textarea value={form.message} onChange={(e) => setField("message", e.target.value)} rows={4} placeholder="Anything else we should know — references, links, context…" className={`${inputCls} resize-y`} data-testid="gq-input-message" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Reference File (optional)</label>
                                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf" onChange={onFilePick} className="hidden" data-testid="gq-input-file" />
                                    {file ? (
                                        <div className="flex items-center gap-3 bg-black/40 border border-gold/30 rounded-xl px-4 py-3" data-testid="gq-file-chip">
                                            <Paperclip size={15} className="text-gold flex-shrink-0" />
                                            <span className="font-inter text-sm text-white truncate flex-1">{file.name}</span>
                                            <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid="gq-file-remove" aria-label="Remove file">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button type="button" onClick={() => fileRef.current?.click()} data-testid="gq-file-pick" className="w-full flex items-center justify-center gap-2 border border-dashed border-white/15 hover:border-gold/50 rounded-xl px-4 py-4 font-inter text-sm text-zinc-400 hover:text-gold transition-colors">
                                            <Paperclip size={15} />
                                            Attach a logo, sample or brief (image / PDF, up to 5 MB)
                                        </button>
                                    )}
                                </div>
                                <input type="text" value={form.website} onChange={(e) => setField("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="website" />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="gq-submit-btn"
                                className="mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 disabled:cursor-not-allowed text-black font-medium font-inter px-9 py-4 rounded-full transition-all duration-200 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" /> Sending…
                                    </>
                                ) : (
                                    <>
                                        Request My Quote <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                            <p className="mt-4 font-inter text-xs text-zinc-500">
                                By submitting, you agree to be contacted on the number provided. No spam, ever.
                            </p>
                        </motion.form>
                    )}
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default GetQuotePage;
